import { SelectCol, SortCol, Filter, QueryFunction, } from "./query.js";
export var QueryType;
(function (QueryType) {
    QueryType[QueryType["Single"] = 0] = "Single";
    QueryType[QueryType["List"] = 1] = "List";
    QueryType[QueryType["Search"] = 2] = "Search";
    QueryType[QueryType["AutoComplete"] = 3] = "AutoComplete";
    QueryType[QueryType["Full"] = 4] = "Full";
    QueryType[QueryType["FullWithSingleRefs"] = 5] = "FullWithSingleRefs";
    QueryType[QueryType["FullNonPersonal"] = 6] = "FullNonPersonal";
})(QueryType || (QueryType = {}));
export class QueryParams {
    entity;
    id;
    entityDef;
    entityDefId;
    selectCols;
    includes;
    filters;
    query;
    startIndex;
    count;
    sortCols;
    searchText;
    columnFilters;
    calcTotalCount;
    queryType;
    disableTransaction;
    entityId;
    propertyName; //alias for name
    propName; //alias for name
    mapColName; //alias for name
    name;
    get refColName() {
        return this.mapColName;
    }
    set refColName(v) {
        this.mapColName = v;
    }
    get entDefId() {
        if (this.entityDef)
            return this.entityDef.id;
    }
    set entDefId(v) {
        if (!this.entityDef)
            this.entityDef = {};
        this.entityDef.id = v;
    }
    get entDefName() {
        if (this.entityDef)
            return this.entityDef.name;
    }
    set entDefName(v) {
        if (!this.entityDef)
            this.entityDef = {};
        this.entityDef.name = v;
    }
    get self() {
        return this;
    }
    getPropMatch(conditionFn) {
        const conditionString = conditionFn.toString().trim();
        // Parameter names are not part of the selector contract: bundlers rename
        // them. Bind the property path to the callback's actual single parameter.
        const propertyMatch = conditionString.match(/^\(?\s*([A-Za-z_$][\w$]*)\s*\)?\s*=>\s*(?:\{\s*return\s+)?\1\.([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*)\s*;?\s*\}?$/) ??
            conditionString.match(/^function(?:\s+[A-Za-z_$][\w$]*)?\s*\(\s*([A-Za-z_$][\w$]*)\s*\)\s*\{\s*return\s+\1\.([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*)\s*;?\s*\}$/);
        if (!propertyMatch) {
            throw new Error("Invalid property expression");
        }
        return propertyMatch[2];
    }
    resolveColumn(col) {
        if (typeof col !== "function")
            return col;
        const property = this.getPropMatch(col);
        if (!property) {
            throw new Error("Unable to extract property name from function expression");
        }
        return property;
    }
    type(queryType) {
        this.queryType = queryType;
        return this;
    }
    take(count) {
        this.count = count;
        return this;
    }
    limit(count) {
        this.count = count;
        return this;
    }
    skip(count) {
        this.startIndex = count;
        return this;
    }
    filter(predicate, value, queryFunction = QueryFunction.Equals, relation) {
        if (!this.filters) {
            this.filters = [];
        }
        // Case 1: predicate is already a Filter object
        if (predicate instanceof Filter) {
            this.filters.push(predicate);
            return this;
        }
        if (typeof predicate !== "string" && typeof predicate !== "function") {
            throw new Error("Invalid filter predicate type");
        }
        const filter = new Filter(this.resolveColumn(predicate), value, queryFunction);
        filter.relation = relation;
        this.filters.push(filter);
        return this;
    }
    /** Unclassified rows (null `isSystem`) belong to "mine", so legacy tenant assets stay visible. */
    ownership(scope) {
        if (scope === "mine") {
            return this.filter(new Filter("isSystem", true).not());
        }
        if (scope === "system")
            return this.filter("isSystem", true);
        return this;
    }
    search(searchText) {
        this.searchText = searchText;
        return this;
    }
    select(col, options) {
        if (!this.selectCols)
            this.selectCols = [];
        const columns = Array.isArray(col) ? col : [this.resolveColumn(col)];
        for (const name of columns) {
            let selCol = this.selectCols.find((p) => p.name === name);
            if (!selCol) {
                selCol = new SelectCol(name);
                this.selectCols.push(selCol);
            }
            if (options) {
                Object.assign(selCol, options);
            }
        }
        return this;
    }
    include(...colNames) {
        if (!this.includes) {
            this.includes = [];
        }
        if (!colNames || colNames.length === 0) {
            return this;
        }
        let inc = null;
        for (const col of colNames) {
            if (col instanceof IncludeQuery) {
                // Handle the case where an IncludeQuery is passed directly
                this.includes.push(col);
                inc = col;
            }
            else {
                const name = this.resolveColumn(col);
                let includeQuery = this.includes.find((p) => p.propertyName === name);
                if (!includeQuery) {
                    includeQuery = new IncludeQuery(name);
                    this.includes.push(includeQuery);
                }
                inc = includeQuery;
            }
        }
        return Object.assign(this, { inc });
    }
    sortBy(col, sortType) {
        const name = this.resolveColumn(col);
        if (!this.sortCols) {
            this.sortCols = [];
        }
        const selCol = this.sortCols.find((p) => p.col?.name === name);
        if (!selCol) {
            const inc = new SelectCol(name);
            const sort = new SortCol();
            sort.col = inc;
            sort.sortType = sortType;
            this.sortCols.push(sort);
        }
        return this;
    }
    pickEntity(entityId) {
        this.entityId = entityId;
        return this;
    }
    returnCount() {
        this.calcTotalCount = true;
        return this;
    }
    setName(name) {
        this.name = name;
        return this;
    }
    constructor(definition, includes) {
        if (definition) {
            if (typeof definition === "string") {
                this.entDefName = definition;
            }
            else {
                const instance = new definition();
                if ("_entDefName" in instance &&
                    typeof instance._entDefName === "string") {
                    this.entDefName = instance._entDefName;
                }
                else {
                    this.entDefName = instance.constructor.name;
                }
            }
        }
        if (includes) {
            this.includes = includes;
        }
    }
}
export class IncludeQuery extends QueryParams {
    constructor(propertyName) {
        super("");
        this.propertyName = propertyName;
    }
}
export class EntityQueryParams extends QueryParams {
    constructor(definition, includes = undefined) {
        super(definition, includes);
    }
}
//# sourceMappingURL=query-params.js.map